import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import { Badge, type BadgeVariant } from '~/components/ui/Badge';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import buttonStyles from '~/styles/button.module.css';
import { RejectApplicationDialog } from './RejectApplicationDialog';

type ApplicationStatus = components['schemas']['ApplicationStatus'];
type HelperApplicationRole = components['schemas']['HelperApplicationRole'];

const statusVariant: Record<ApplicationStatus, BadgeVariant> = {
  approved: 'success',
  rejected: 'error',
  pending: 'warning',
};

// Which of the three intake forms this row came from, and therefore which
// polymorphic identity Approve mints -- `service.py:_create_follow_up_user`
// picks the class off `application.role`. Spellings match the queue's own map
// (`applications.index.tsx`), duplicated rather than shared because `admin` has
// no i18n and the two route files are separate lanes; three literals.
const roleLabels: Record<HelperApplicationRole, string> = {
  STUDY_HELPER: 'Study Helper',
  FOLLOWUP_COORDINATOR: 'Follow-up Coordinator',
  FOLLOWUP_PARTNER: 'Follow-up Partner',
};

export const Route = createFileRoute('/_admin/applications/$applicationId')({
  component: ApplicationDetail,
});

const formatLabel = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

function ApplicationDetail() {
  const { applicationId } = Route.useParams();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');

  const {
    data: application,
    isLoading,
    error,
  } = api.getHelperApplication.useQuery({
    path: { application_id: Number(applicationId) },
  });

  const approveMutation = api.approveHelperApplication.useMutation(undefined, {
    meta: { toast: 'Approved' },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.getHelperApplication.getQueryKey({
          path: { application_id: Number(applicationId) },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.listHelperApplications.getQueryKey(),
      });
    },
  });

  const rejectMutation = api.rejectHelperApplication.useMutation(undefined, {
    meta: { toast: 'Rejected' },
    onSuccess: () => {
      setRejectDialogOpen(false);
      setRejectMessage('');
      queryClient.invalidateQueries({
        queryKey: api.getHelperApplication.getQueryKey({
          path: { application_id: Number(applicationId) },
        }),
      });
      queryClient.invalidateQueries({
        queryKey: api.listHelperApplications.getQueryKey(),
      });
    },
  });

  const handleApprove = () => {
    approveMutation.mutate({
      path: { application_id: Number(applicationId) },
    });
  };

  const handleReject = () => {
    rejectMutation.mutate({
      path: { application_id: Number(applicationId) },
      body: { rejection_reason: rejectMessage },
    });
  };

  if (isLoading) {
    return <LoadingMessage>Loading application...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error loading application</ErrorMessage>;
  }

  if (!application) {
    return <ErrorMessage>Application not found</ErrorMessage>;
  }

  const isPending = application.status === 'pending';
  const isFollowUp = application.role !== 'STUDY_HELPER';
  // Key-agnostic on purpose: the follow-up forms answer four screening
  // questions (GF 12 fields 74/75/76/83, GF 14 fields 68/69/70/87) whose keys
  // are the request's own field names, and GF-4 study-helper rows carry `{}`.
  const screeningResponses = Object.entries(application.screening_responses);

  return (
    <Container>
      <BackLink to="/applications">&larr; Back to Applications</BackLink>

      <Header>
        <TitleGroup>
          <PageTitle>
            {application.first_name} {application.last_name}
          </PageTitle>
          <Badge $variant="neutral" $size="md">
            {roleLabels[application.role]}
          </Badge>
        </TitleGroup>
        {isPending ? (
          <ActionButtons>
            <Button
              className={buttonStyles.Button}
              onClick={handleApprove}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
            <RejectApplicationDialog
              open={rejectDialogOpen}
              onOpenChange={setRejectDialogOpen}
              rejectMessage={rejectMessage}
              onRejectMessageChange={setRejectMessage}
              onReject={handleReject}
              isPending={rejectMutation.isPending}
              disabled={approveMutation.isPending || rejectMutation.isPending}
            />
          </ActionButtons>
        ) : (
          <Badge $variant={statusVariant[application.status]} $size="md">
            {application.status}
          </Badge>
        )}
      </Header>

      <Section>
        <SectionTitle>Personal Information</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Email</FieldLabel>
            <FieldValue>{application.email}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Phone</FieldLabel>
            <FieldValue>{application.phone || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>WhatsApp</FieldLabel>
            <FieldValue>{application.whatsapp || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Gender</FieldLabel>
            <FieldValue>{application.gender || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Birthdate</FieldLabel>
            <FieldValue>
              {application.birthdate
                ? new Date(application.birthdate).toLocaleDateString()
                : '—'}
            </FieldValue>
          </Field>
          {/* Both age signals, side by side, because exactly one of them is
              ever populated: GF 12/14 ask for a birth YEAR, so `birthdate` is
              NULL by construction on every follow-up row (DIV-4,
              `helper_applications/models.py`). Rendering only Birthdate showed
              the approver an em-dash where the answer exists. */}
          <Field>
            <FieldLabel>Birth Year</FieldLabel>
            <FieldValue>{application.birth_year ?? '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Preferred Languages</FieldLabel>
            <FieldValue>
              {application.preferred_languages?.join(', ') || '—'}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>Address</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Address Line 1</FieldLabel>
            <FieldValue>{application.address_1 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Address Line 2</FieldLabel>
            <FieldValue>{application.address_2 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>City</FieldLabel>
            <FieldValue>{application.city || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>State</FieldLabel>
            <FieldValue>{application.state || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>ZIP</FieldLabel>
            <FieldValue>{application.zip || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Country</FieldLabel>
            <FieldValue>{application.country || '—'}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>Congregation Information</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Congregation Name</FieldLabel>
            <FieldValue>{application.congregation_name || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Website</FieldLabel>
            <FieldValue>{application.congregation_website || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Address Line 1</FieldLabel>
            <FieldValue>{application.congregation_address_1 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Address Line 2</FieldLabel>
            <FieldValue>{application.congregation_address_2 || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>City</FieldLabel>
            <FieldValue>{application.congregation_city || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>State</FieldLabel>
            <FieldValue>{application.congregation_state || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>ZIP</FieldLabel>
            <FieldValue>{application.congregation_zip || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Country</FieldLabel>
            <FieldValue>{application.congregation_country || '—'}</FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      {isFollowUp && (
        <Section>
          <SectionTitle>Follow-up Assignment</SectionTitle>
          <FieldGrid>
            {/* THE APPROVAL-CRITICAL FIELD. Approve copies this list onto the
                created account's `approved_countries`
                (`service.py:_create_follow_up_user`), which is the column
                W4.7's network directory and W4.8's Nations cell read -- so with
                it unrendered the administrator approved the coordinator's
                serving countries blind. Raw codes, like the Country fields
                above and below: this page has no country-name lookup and
                adding one for a single field would leave seven coded and one
                named. An em-dash is real signal, not a gap -- 8 of 14 migrated
                GF-14 entries carry no field 67 at all
                (`helper_applications/models.py`), and approving one of those
                mints an account serving no country. */}
            <Field>
              <FieldLabel>Countries Served</FieldLabel>
              <FieldValue>
                {application.follow_up_countries?.length
                  ? application.follow_up_countries.join(', ')
                  : '—'}
              </FieldValue>
            </Field>
            {/* GF 12 `82.1` / GF 14 `86.1`. No destination on `users`, so this
                row is the only store (§10.8) and this page is the only reader
                anyone gets. */}
            <Field>
              <FieldLabel>Contact Sharing Consent</FieldLabel>
              <FieldValue>
                {application.share_contact_consent === null
                  ? '—'
                  : application.share_contact_consent
                    ? 'Yes'
                    : 'No'}
              </FieldValue>
            </Field>
          </FieldGrid>
        </Section>
      )}

      <Section>
        <SectionTitle>Radio Responses</SectionTitle>
        {Object.entries(application.radio_responses).map(([key, value]) => (
          <AgreementItem key={key}>
            <AgreementLabel>{formatLabel(key)}</AgreementLabel>
            <Badge $variant={value === 'agree' ? 'success' : 'neutral'}>
              {formatLabel(value)}
            </Badge>
          </AgreementItem>
        ))}
      </Section>

      {screeningResponses.length > 0 && (
        <Section>
          <SectionTitle>Screening</SectionTitle>
          <ScreeningList>
            {screeningResponses.map(([key, value]) => (
              <Field key={key}>
                <FieldLabel>{formatLabel(key)}</FieldLabel>
                <ScreeningAnswer>{value}</ScreeningAnswer>
              </Field>
            ))}
          </ScreeningList>
        </Section>
      )}

      <Section>
        <SectionTitle>Additional Information</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Referral Source</FieldLabel>
            <FieldValue>{application.referral_source || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Has Existing Account</FieldLabel>
            <FieldValue>
              {application.has_existing_account === null
                ? '—'
                : application.has_existing_account
                  ? 'Yes'
                  : 'No'}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Existing Account Email</FieldLabel>
            <FieldValue>{application.existing_account_email || '—'}</FieldValue>
          </Field>
        </FieldGrid>
        {application.comments_motivation && (
          <CommentsField>
            <FieldLabel>Comments / Motivation</FieldLabel>
            <FieldValue>{application.comments_motivation}</FieldValue>
          </CommentsField>
        )}
      </Section>

      <Section>
        <SectionTitle>Timestamps</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>Created At</FieldLabel>
            <FieldValue>
              {new Date(application.created_at).toLocaleString()}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Updated At</FieldLabel>
            <FieldValue>
              {new Date(application.updated_at).toLocaleString()}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>
    </Container>
  );
}

// Styles

const Container = styled.div``;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--blue);
  text-decoration: none;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const AgreementItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--inner-padding);
  border-bottom: var(--component-border);

  &:last-child {
    border-bottom: none;
  }
`;

const AgreementLabel = styled.span`
  font-size: 0.875rem;
  color: var(--grey-800);
`;

const ScreeningList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--inner-padding);
`;

// Not the agreements' label-plus-Badge row: a screening answer is free text up
// to 2000 characters, so it stacks under its label and keeps its own newlines.
const ScreeningAnswer = styled(FieldValue)`
  white-space: pre-wrap;
`;

const CommentsField = styled(Field)`
  margin-top: 1rem;
  padding: 0 var(--inner-padding) var(--inner-padding);
`;

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
